import axiosInstance from '../../../lib/api/axios-instance';
import { getBusinessId } from '../../../lib/api/axios-instance';

// ===========================
// Type Definitions
// ===========================

export interface Transaction {
  id: string;
  operation_id: string;
  transaction_ref: string;
  transaction_date: string;
  transaction_time: string;
  amount: string | number;
  currency: string;
  bank_code: string;
  sender_account_number: string | null;
  sender_account_name: string | null;
  receiver_account_number: string | null;
  receiver_account_name: string | null;
  verification_status: 'verified' | 'failed' | 'duplicate' | 'pending';
  is_duplicate: boolean;
  duplicate_of_id: string | null;
  transaction_type: string;
  classification_confidence: string;
  channel: 'API' | 'MANUAL' | 'LINE';
  credits_used: number;
  credits_saved: number;
  api_cost: string;
  processing_time_ms: number | null;
  branch_id: string | null;
  account_id: string | null;
  created_at: string;
  updated_at: string;
  // Optional fields for UI (from JOINs)
  branch_name?: string;
  branch_code?: string;
  account_display_name?: string;
  account_bank_code?: string;
  account_number_display?: string;
  business_name?: string;
}

export interface TransactionListResponse {
  success: boolean;
  data: {
    transactions: Transaction[];
    pagination: {
      page: number;
      limit: number;
      total_records: number;
      total_pages: number;
      has_next: boolean;
      has_prev: boolean;
    };
    filters_applied?: any;
    sorting?: any;
    summary?: {
      total_amount: number;
      total_credits_used: number;
      total_credits_saved: number;
      duplicate_count: number;
    };
  };
  metadata?: {
    processing_time_ms: number;
    timestamp: string;
  };
}

export interface TransactionDetailResponse {
  success: boolean;
  data: {
    transaction: Transaction;
    duplicate_transactions?: Transaction[];
  };
}

export interface TransactionAnalytics {
  summary: {
    total_transactions: number;
    total_amount: number;
    date_range: {
      start_date: string;
      end_date: string;
    };
  };
  verification: {
    verified_count: number;
    failed_count: number;
    pending_count: number;
    success_rate: number;
  };
  deduplication: {
    duplicate_detected: number;
    duplicate_blocked: number;
    unique_transactions: number;
  };
  credits: {
    total_credits_used: number;
    average_credit_per_transaction: number;
  };
  channel_breakdown: {
    LINE: number;
    MANUAL: number;
    API: number;
  };
  daily_stats?: Array<{
    date: string;
    count: number;
    amount: number;
    verified: number;
    failed: number;
  }>;
}

export interface TransactionAnalyticsResponse {
  success: boolean;
  data: TransactionAnalytics;
}

export interface ApiUsageLog {
  id: string;
  business_id: string;
  endpoint: string;
  method: string;
  status_code: number;
  response_time: number;
  requested_ip: string;
  allowed_ip?: string;
  api_key_used?: string;
  request_timestamp: string;
  error_message?: string;
}

export interface ApiUsageListResponse {
  success: boolean;
  data: ApiUsageLog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Filter and Pagination Types
export interface TransactionFilters {
  page?: number;
  limit?: number;
  channel?: 'LINE' | 'MANUAL' | 'API' | 'all';
  status?: 'verified' | 'failed' | 'pending' | 'all';
  startDate?: string;
  endDate?: string;
  search?: string;
  branch_id?: string;
}

export interface ApiUsageFilters {
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
  search?: string;
  status_code?: number;
}

// ===========================
// API Functions
// ===========================

export const transactionsAPI = {
  /**
   * Get list of transactions with filters and pagination
   */
  async getTransactions(filters: TransactionFilters = {}): Promise<TransactionListResponse> {
    const businessId = getBusinessId();
    if (!businessId) {
      throw new Error('Business ID not found. Please log in again.');
    }

    const params = new URLSearchParams();
    
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.channel && filters.channel !== 'all') params.append('channel', filters.channel);
    if (filters.status && filters.status !== 'all') params.append('status', filters.status);
    if (filters.startDate) params.append('date_from', filters.startDate);
    if (filters.endDate) params.append('date_to', filters.endDate);
    if (filters.search) params.append('search', filters.search);
    if (filters.branch_id) params.append('branch_id', filters.branch_id);

    const response = await axiosInstance.get<TransactionListResponse>(
      `/api/v2/business/${businessId}/transactions?${params.toString()}`
    );

    return response.data;
  },

  /**
   * Get single transaction details by ID
   */
  async getTransactionById(transactionId: string): Promise<TransactionDetailResponse> {
    const businessId = getBusinessId();
    if (!businessId) {
      throw new Error('Business ID not found. Please log in again.');
    }

    const response = await axiosInstance.get<TransactionDetailResponse>(
      `/api/v2/business/${businessId}/transactions/${transactionId}`
    );

    return response.data;
  },

  /**
   * Get transaction analytics summary
   */
  async getAnalytics(startDate?: string, endDate?: string): Promise<TransactionAnalyticsResponse> {
    const businessId = getBusinessId();
    if (!businessId) {
      throw new Error('Business ID not found. Please log in again.');
    }

    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await axiosInstance.get<TransactionAnalyticsResponse>(
      `/api/v2/business/${businessId}/transactions/analytics/summary?${params.toString()}`
    );

    return response.data;
  },

  /**
   * Get API usage logs (if endpoint exists)
   * Note: This endpoint may need to be implemented on backend
   */
  async getApiUsageLogs(filters: ApiUsageFilters = {}): Promise<ApiUsageListResponse> {
    const businessId = getBusinessId();
    if (!businessId) {
      throw new Error('Business ID not found. Please log in again.');
    }

    const params = new URLSearchParams();
    
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.search) params.append('search', filters.search);
    if (filters.status_code) params.append('status_code', filters.status_code.toString());

    const response = await axiosInstance.get<ApiUsageListResponse>(
      `/api/v2/business/${businessId}/api-usage-logs?${params.toString()}`
    );

    return response.data;
  },

  /**
   * Export transactions to file (CSV or Excel)
   */
  async exportTransactions(
    format: 'csv' | 'excel',
    filters: TransactionFilters = {}
  ): Promise<Blob> {
    const businessId = getBusinessId();
    if (!businessId) {
      throw new Error('Business ID not found. Please log in again.');
    }

    const params = new URLSearchParams();
    params.append('format', format);
    
    if (filters.channel && filters.channel !== 'all') params.append('channel', filters.channel);
    if (filters.status && filters.status !== 'all') params.append('status', filters.status);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.search) params.append('search', filters.search);
    if (filters.branch_id) params.append('branch_id', filters.branch_id);

    const response = await axiosInstance.get(
      `/api/v2/business/${businessId}/transactions/export?${params.toString()}`,
      {
        responseType: 'blob',
      }
    );

    return response.data;
  },
};

// Export default
export default transactionsAPI;
